import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { AppLoader, AppSnackbar } from "../../../../components/common";
import { useAuth } from "../../../../contexts/AuthContext";

import OperationActionDialog from "../components/OperationActionDialog";
import OperationCreateDialog from "../components/OperationCreateDialog";
import OperationDetailsDialog from "../components/OperationDetailsDialog";
import OperationPanel from "../components/OperationPanel";
import OperationsFilters from "../components/OperationsFilters";
import OperationsHeader from "../components/OperationsHeader";
import OperationsSummaryCards from "../components/OperationsSummaryCards";
import OperationsTabs from "../components/OperationsTabs";
import useOperations from "../hooks/useOperations";
import { OperationActionConfig, OperationRecord, OperationTab } from "../types";
import { filterRowsBySearch } from "../../utils/rowSearch";
import { sparePageSx } from "../../components/SparePagePresentation";

const panelCopy: Record<OperationTab, { title: string; description: string }> = {
  requests: {
    title: "Stock Requests",
    description: "Requests raised by hubs or service teams for spare availability.",
  },
  transfers: {
    title: "Stock Transfers",
    description: "Hub-to-hub spare movement from dispatch to receipt.",
  },
  receipts: {
    title: "Goods Receipts",
    description: "Receipt confirmation and verification for inbound stock.",
  },
  issues: {
    title: "Spare Issues",
    description: "Spare parts issued from maintenance tickets with inventory movement details.",
  },
  returns: {
    title: "Spare Returns",
    description: "Technician spare returns awaiting AME approval before stock is received.",
  },
  spareUsage: {
    title: "Service Spare Usage",
    description: "Actual spare consumption recorded from maintenance ticket completion.",
  },
  invoices: {
    title: "Service Invoices",
    description: "Invoice records generated from service labor and spare consumption.",
  },
};

const manualCreateTabs = new Set<OperationTab>([
  "requests",
  "transfers",
  "receipts",
  "invoices",
]);

const operationTabs = new Set<OperationTab>([
  "requests",
  "transfers",
  "receipts",
  "issues",
  "returns",
  "spareUsage",
  "invoices",
]);

const OperationsPage = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const {
    records,
    activeRows,
    summary,
    options,
    filters,
    loading,
    actionLoading,
    error,
    success,
    refresh,
    updateFilters,
    runAction,
    createOperation,
    clearError,
    clearSuccess,
  } = useOperations();
  const [selectedRecord, setSelectedRecord] = useState<OperationRecord | null>(null);
  const [viewRecord, setViewRecord] = useState<OperationRecord | null>(null);
  const [selectedAction, setSelectedAction] = useState<OperationActionConfig | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createTab, setCreateTab] = useState<OperationTab>("requests");
  const [transferRequest, setTransferRequest] = useState<OperationRecord | null>(null);
  const requestedTab = searchParams.get("tab");

  useEffect(() => {
    if (
      requestedTab &&
      operationTabs.has(requestedTab as OperationTab) &&
      filters.tab !== requestedTab
    ) {
      updateFilters({ tab: requestedTab as OperationTab });
    }
  }, [filters.tab, requestedTab, updateFilters]);

  const openAction = (record: OperationRecord, action: OperationActionConfig) => {
    if (action.key === "createTransfer") {
      setCreateTab("transfers");
      setTransferRequest(record);
      setCreateOpen(true);
      return;
    }

    setSelectedRecord(record);
    setSelectedAction(action);
  };

  const closeAction = () => {
    setSelectedRecord(null);
    setSelectedAction(null);
  };

  const confirmAction = async (remarks: string, actorId: string) => {
    if (!selectedRecord || !selectedAction) return;

    await runAction(selectedRecord, selectedAction.key, remarks, actorId);
    closeAction();
  };

  if (loading && !Object.values(records).some((rows) => rows.length)) {
    return <AppLoader />;
  }

  return (
    <Box sx={sparePageSx("operations")}>
      <OperationsHeader
        filters={filters}
        loading={loading}
        canCreate={manualCreateTabs.has(filters.tab)}
        onRefresh={() => refresh()}
        onCreate={() => {
          setCreateTab(filters.tab);
          setTransferRequest(null);
          setCreateOpen(true);
        }}
      />

      <Box mt={2}>
        <OperationsSummaryCards summary={summary} records={records} />
      </Box>

      <Box mt={2}>
        <OperationsFilters
          filters={filters}
          options={options}
          onChange={updateFilters}
          onReset={() =>
            updateFilters({
              search: "",
              status: "",
              warehouseId: "",
              technicianId: "",
            })
          }
        />
      </Box>

      <Box mt={2}>
        <OperationsTabs
          value={filters.tab}
          records={records}
          onChange={(tab) => updateFilters({ tab })}
        />
      </Box>

      <OperationPanel
        tab={filters.tab}
        rows={filterRowsBySearch(activeRows, filters.search)}
        loading={loading}
        inventoryStocks={options.inventoryStocks}
        stockTransfers={records.transfers}
        onAction={openAction}
        title={panelCopy[filters.tab].title}
        description={panelCopy[filters.tab].description}
        onView={setViewRecord}
      />

      <OperationActionDialog
        open={!!selectedRecord && !!selectedAction}
        record={selectedRecord}
        action={selectedAction}
        actors={options.ames}
        loading={actionLoading}
        onClose={closeAction}
        onConfirm={confirmAction}
      />

      <OperationCreateDialog
        open={createOpen}
        tab={createTab}
        options={options}
        initialStockRequest={transferRequest}
        currentUserId={user?.employeeId || user?.id || ""}
        loading={actionLoading}
        onClose={() => {
          setCreateOpen(false);
          setTransferRequest(null);
        }}
        onSubmit={async (form) => {
          await createOperation(form);
          setCreateOpen(false);
          setTransferRequest(null);
        }}
      />

      <OperationDetailsDialog
        open={!!viewRecord}
        record={viewRecord}
        onClose={() => setViewRecord(null)}
      />

      <AppSnackbar
        open={!!error}
        severity="error"
        message={error || ""}
        onClose={clearError}
      />
      <AppSnackbar
        open={!!success}
        severity="success"
        message={success || ""}
        onClose={clearSuccess}
      />
    </Box>
  );
};

export default OperationsPage;
